import { useMemo } from "react"

import { DEFAULT_CURRENCY, formatStripeAmount, useBounceGlobalValues, useTranslation } from "@bounce/i18n"

import type { CityData } from "../../../../libs/bounce/cities"
import { getCityCurrency } from "../../../../utils/city"
import { defaultPricing } from "../../../../utils/pricing"

export type Interpolations = {
  PRICE: string
  LOWEST_PRICE: string
  INSURANCE_AMOUNT: string
  NUM_BAGS_STORED: string
  NUM_CITIES: string
  NUM_LOCATIONS: string
  CITY_NAME: string
  YEAR: string
  CITY_MAIN_STORE_LOCATION: string
}

export const useInterpolations = ({ city }: { city?: CityData | null }): Interpolations => {
  const { i18n } = useTranslation()
  const bounceGlobals = useBounceGlobalValues({
    currency: city ? getCityCurrency(city) : DEFAULT_CURRENCY,
  })

  const interpolations = useMemo(() => {
    const { defaultLowestLuggagePrice, defaultCurrency } = defaultPricing

    const lowestLuggagePrice = city?.lowestPrice || defaultLowestLuggagePrice
    const currency = city?.luggagePrice ? city.currency : defaultCurrency
    const { bounceShieldProtection, numLocations, numCities, numBagsStored } = bounceGlobals

    const lowestPrice = formatStripeAmount(lowestLuggagePrice, currency, i18n.language)

    return {
      PRICE: lowestPrice,
      LOWEST_PRICE: lowestPrice,
      INSURANCE_AMOUNT: bounceShieldProtection,
      NUM_BAGS_STORED: numBagsStored,
      NUM_CITIES: numCities,
      NUM_LOCATIONS: numLocations,
      CITY_NAME: city?.translatedName || "",
      YEAR: new Date().getFullYear().toString(),
      CITY_MAIN_STORE_LOCATION: city?.mainStore?.gmbAddress?.addressLines || city?.translatedName || "",
      STORAGE_SPOT_COUNT: city?.storeCount,
    }
  }, [bounceGlobals, city, i18n.language])

  return interpolations
}
